export interface ClientContextPayload {
  userAgent?: string;
  devicePlatform?: string;
  osVersion?: string;
  language?: string;
  locale?: string;
  screenResolution?: string;
  viewportSize?: string;
}

interface UaHighEntropyValues {
  platform?: string;
  platformVersion?: string;
}

interface NavigatorUaData {
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<UaHighEntropyValues>;
}

const contextByConversation = new Map<string, Promise<ClientContextPayload>>();

function inferPlatformFromUserAgent(ua: string): string | undefined {
  if (/Windows/i.test(ua)) return 'Windows';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/Mac OS X|Macintosh/i.test(ua)) return 'macOS';
  if (/CrOS/i.test(ua)) return 'Chrome OS';
  if (/Linux/i.test(ua)) return 'Linux';
  return undefined;
}

export function collectClientContext(conversationId: string): Promise<ClientContextPayload> {
  const cached = contextByConversation.get(conversationId);
  if (cached) return cached;

  const context = readClientContext();
  contextByConversation.set(conversationId, context);
  return context;
}

async function readClientContext(): Promise<ClientContextPayload> {
  const ctx: ClientContextPayload = {
    userAgent: navigator.userAgent || undefined,
    language: navigator.language || undefined,
  };

  try {
    if (typeof screen !== 'undefined' && screen.width && screen.height) {
      ctx.screenResolution = `${screen.width}x${screen.height}`;
    }
    if (typeof window !== 'undefined' && window.innerWidth && window.innerHeight) {
      ctx.viewportSize = `${window.innerWidth}x${window.innerHeight}`;
    }
  } catch {
  }

  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUaData }).userAgentData;
  if (uaData?.getHighEntropyValues) {
    try {
      const high = await uaData.getHighEntropyValues(['platform', 'platformVersion']);
      ctx.devicePlatform = high.platform || uaData.platform || undefined;
      ctx.osVersion = high.platformVersion || undefined;
    } catch {
      ctx.devicePlatform = uaData.platform || undefined;
    }
  }

  if (!ctx.devicePlatform) {
    ctx.devicePlatform = inferPlatformFromUserAgent(navigator.userAgent || '');
  }

  return ctx;
}
